import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    MenuItem,
    IconButton,
    Checkbox,
    FormControlLabel,
    CircularProgress,
    Alert,
    Divider,
} from "@mui/material";
import { Save, Add, DeleteOutlineOutlined, OpenInNew } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import systemSettingsService from "../../services/systemSettingsService";
import PageHeader from "../../components/common/PageHeader";
import { PREMIUM_FEATURE_ICONS } from "../customer/CustomerUpgrade";

const ICON_OPTIONS = Object.keys(PREMIUM_FEATURE_ICONS);

let nextId = 0;
const uid = () => `${Date.now()}-${nextId++}`;

const emptyFeature = () => ({ _id: uid(), icon: "star", title: "", description: "", comingSoon: false });

// Edits SystemSettings.premiumPageContent — the heading/pricing copy and
// feature list the customer-facing Upgrade page (CustomerUpgrade.jsx)
// renders. Nothing here is wired to real billing; it's just the content of
// that preview page, same as Integrations/Invoice Rates/etc are plain
// settings sub-pages.
const AdminUpgradePage = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [heading, setHeading] = useState("");
    const [priceText, setPriceText] = useState("");
    const [ctaText, setCtaText] = useState("");
    const [features, setFeatures] = useState([]);

    useEffect(() => {
        systemSettingsService
            .getPremiumPageContent()
            .then(({ data }) => {
                const content = data.data.premiumPageContent || {};
                setHeading(content.heading || "");
                setPriceText(content.priceText || "");
                setCtaText(content.ctaText || "");
                setFeatures((content.features || []).map((f) => ({ ...f, _id: uid() })));
            })
            .catch(() => enqueueSnackbar("Failed to load the Upgrade page content", { variant: "error" }))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateFeature = (id, patch) =>
        setFeatures((prev) => prev.map((f) => (f._id === id ? { ...f, ...patch } : f)));
    const addFeature = () => setFeatures((prev) => [...prev, emptyFeature()]);
    const removeFeature = (id) => setFeatures((prev) => prev.filter((f) => f._id !== id));

    const handleSave = async () => {
        if (features.some((f) => !f.title.trim())) {
            enqueueSnackbar("Every feature needs a title (remove any empty ones)", { variant: "warning" });
            return;
        }
        setSaving(true);
        try {
            await systemSettingsService.update({
                premiumPageContent: {
                    heading,
                    priceText,
                    ctaText,
                    features: features.map(({ icon, title, description, comingSoon }) => ({
                        icon,
                        title,
                        description,
                        comingSoon,
                    })),
                },
            });
            enqueueSnackbar("Upgrade page saved", { variant: "success" });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to save", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <PageHeader
                title="Upgrade Page"
                subtitle="Edit the content customers see on the Upgrade to Premium page"
                action={
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            startIcon={<OpenInNew />}
                            onClick={() => window.open("/dashboard/upgrade", "_blank")}
                        >
                            View Live Page
                        </Button>
                        <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving || loading}>
                            {saving ? <CircularProgress size={18} color="inherit" /> : "Save"}
                        </Button>
                    </Box>
                }
            />

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                        This page is a preview only — there's no signup/checkout flow yet, so the button on it always
                        shows as disabled regardless of the CTA text below.
                    </Alert>

                    <Card>
                        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                            <Typography variant="overline" color="text.secondary">
                                Header
                            </Typography>
                            <TextField
                                label="Heading"
                                value={heading}
                                onChange={(e) => setHeading(e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Price / subtext"
                                value={priceText}
                                onChange={(e) => setPriceText(e.target.value)}
                                fullWidth
                                helperText="Shown under the heading — pricing isn't final, so keep this as a rough/approximate statement."
                            />
                            <TextField
                                label="Button text"
                                value={ctaText}
                                onChange={(e) => setCtaText(e.target.value)}
                                sx={{ maxWidth: 320 }}
                            />
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <Typography variant="overline" color="text.secondary">
                                    Features
                                </Typography>
                                <Button size="small" startIcon={<Add />} onClick={addFeature}>
                                    Add Feature
                                </Button>
                            </Box>

                            {features.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    No features yet — click "Add Feature" to add the first one.
                                </Typography>
                            )}

                            {features.map((feature, idx) => (
                                <Box key={feature._id}>
                                    {idx > 0 && <Divider sx={{ mb: 2.5 }} />}
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: { xs: "1fr", sm: "140px 1fr 1fr 36px" },
                                            gap: 1.5,
                                            alignItems: "flex-start",
                                        }}
                                    >
                                        <TextField
                                            select
                                            size="small"
                                            label="Icon"
                                            value={feature.icon}
                                            onChange={(e) => updateFeature(feature._id, { icon: e.target.value })}
                                        >
                                            {ICON_OPTIONS.map((key) => (
                                                <MenuItem key={key} value={key}>
                                                    {key}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <TextField
                                            size="small"
                                            label="Title"
                                            value={feature.title}
                                            onChange={(e) => updateFeature(feature._id, { title: e.target.value })}
                                            required
                                        />
                                        <TextField
                                            size="small"
                                            label="Description"
                                            value={feature.description}
                                            onChange={(e) => updateFeature(feature._id, { description: e.target.value })}
                                        />
                                        <IconButton size="small" onClick={() => removeFeature(feature._id)} sx={{ mt: 0.5 }}>
                                            <DeleteOutlineOutlined fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    <FormControlLabel
                                        sx={{ mt: 0.5 }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={!!feature.comingSoon}
                                                onChange={(e) => updateFeature(feature._id, { comingSoon: e.target.checked })}
                                            />
                                        }
                                        label={<Typography variant="body2">Show "Soon" badge on this feature</Typography>}
                                    />
                                </Box>
                            ))}
                        </CardContent>
                    </Card>
                </>
            )}
        </Box>
    );
};

export default AdminUpgradePage;
